import {
    CdkDragDrop,
    CdkDragPlaceholder,
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    moveItemInArray,
} from '@angular/cdk/drag-drop';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../material.module';
import { Organization, OrgPart } from '@newgenus/common';
import { OrgBoardService } from '../../services/org-board-service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { NgStyle } from '@angular/common';

/**
 * Dialog component for reordering children of a parent part in the organization hierarchy.
 *
 * This dialog allows users to drag-and-drop reorder the children of a selected parent part.
 * When saved, it sends an atomic `reorderChildren` command to the backend which:
 * 1. Updates all paths in a single transaction
 * 2. Prevents race conditions that occurred with individual updatePart calls
 * 3. Cascades path updates to all descendants
 *
 * @see REORDER-CHILDREN-ARCHITECTURE.md for full design documentation
 */
@Component({
    selector: 'shared-org-reorder-children-dialog',
    imports: [MaterialModule, CdkDropList, CdkDrag, CdkDragHandle, CdkDragPlaceholder, NgStyle],
    templateUrl: './org-reorder-children-dialog.component.html',
    styleUrls: ['./org-reorder-children-dialog.component.scss'],
})
export class OrgReorderChildrenDialogComponent implements OnInit, OnDestroy {
    /** The parent part whose children will be reordered */
    public parentPart: OrgPart | null = null;

    /** Children of the parent part in their current order */
    public children: OrgPart[] = [];

    /** The original order of children for comparison */
    private originalOrder: string[] = [];

    /** Whether there are unsaved changes */
    public hasChanges = false;

    /** Loading state while saving */
    public isSaving = false;

    /** Error message to display */
    public errorMessage: string | null = null;

    private destroy$ = new Subject<void>();

    public dialogRef = inject(MatDialogRef<OrgReorderChildrenDialogComponent>);
    public data = inject(MAT_DIALOG_DATA) as {
        organization: Organization;
        parentPartKey: string;
    };

    private obService = inject(OrgBoardService);
    private snackbar = inject(MatSnackBar);

    ngOnInit(): void {
        this.loadParentAndChildren();
    }

    /**
     * Load the parent part and its children from the organization data
     */
    private loadParentAndChildren(): void {
        const orgParts = this.data.organization?.orgParts || [];

        // Find the parent part
        this.parentPart = orgParts.find((p) => p.partKey === this.data.parentPartKey) || null;

        if (!this.parentPart) {
            this.errorMessage = 'Parent part not found';
            return;
        }

        // Find all direct children of this parent
        this.children = orgParts
            .filter((p) => p.parentPartKey === this.data.parentPartKey)
            .sort((a, b) => {
                // Sort by path index to get current order (children.X.data where X is the sibling index)
                const aIndex = this.extractIndexFromPath(a.path);
                const bIndex = this.extractIndexFromPath(b.path);
                return aIndex - bIndex;
            });

        // Store original order for comparison
        this.originalOrder = this.children.map((c) => c.partKey);

        if (this.children.length < 2) {
            this.errorMessage = 'Parent must have at least 2 children to reorder';
        }
    }

    /**
     * Extract the sibling index from a path string
     * E.g., "children.0.children.2.data" -> 2
     */
    private extractIndexFromPath(path: string): number {
        if (!path) return 0;

        const pathParts = path.split('.');

        // Work backwards from the end to find the last children.X pattern
        for (let i = pathParts.length - 1; i >= 0; i--) {
            if (pathParts[i] === 'data' && i > 0 && pathParts[i - 1].match(/^\d+$/)) {
                return parseInt(pathParts[i - 1], 10);
            }
        }

        return 0;
    }

    /**
     * Handle drag-and-drop reordering
     */
    onDrop(event: CdkDragDrop<OrgPart[]>): void {
        moveItemInArray(this.children, event.previousIndex, event.currentIndex);
        this.checkForChanges();
    }

    /**
     * Move a child up in the order
     */
    moveUp(index: number): void {
        if (index > 0) {
            moveItemInArray(this.children, index, index - 1);
            this.checkForChanges();
        }
    }

    /**
     * Move a child down in the order
     */
    moveDown(index: number): void {
        if (index < this.children.length - 1) {
            moveItemInArray(this.children, index, index + 1);
            this.checkForChanges();
        }
    }

    /**
     * Check if the current order differs from the original
     */
    private checkForChanges(): void {
        const currentOrder = this.children.map((c) => c.partKey);
        this.hasChanges = !this.arraysEqual(currentOrder, this.originalOrder);
    }

    /**
     * Compare two arrays for equality
     */
    private arraysEqual(a: string[], b: string[]): boolean {
        if (a.length !== b.length) return false;
        return a.every((val, index) => val === b[index]);
    }

    /**
     * Reset to original order
     */
    resetOrder(): void {
        // Reconstruct children in original order
        const orgParts = this.data.organization?.orgParts || [];
        this.children = this.originalOrder
            .map((key) => orgParts.find((p) => p.partKey === key))
            .filter((p): p is OrgPart => p !== undefined);
        this.hasChanges = false;
    }

    /**
     * Save the new order by sending a reorderChildren command
     */
    async saveOrder(): Promise<void> {
        if (!this.hasChanges || !this.parentPart) {
            return;
        }

        this.isSaving = true;
        this.errorMessage = null;

        try {
            const newChildOrder = this.children.map((c) => c.partKey);

            // Send the reorder command to the backend
            await this.obService.reorderChildren(this.data.organization.key, this.data.parentPartKey, newChildOrder);

            // Close dialog immediately with processing flag
            // The hierarchy dialog will show the loader and toast when complete
            this.dialogRef.close({ success: true, newChildOrder, processing: true });
        } catch (error) {
            console.error('Failed to reorder children:', error);
            this.errorMessage = 'Failed to reorder children. Please try again.';
            this.snackbar.open('Failed to reorder children', 'Close', { duration: 3000 });
            this.isSaving = false;
        }
    }

    /**
     * Close without saving
     */
    cancel(): void {
        this.dialogRef.close(null);
    }

    /**
     * Get the display color for a child part
     */
    getChildColor(child: OrgPart): string {
        return child.colour?.background || '#e0e0e0';
    }

    /**
     * Get the text color for a child part
     */
    getTextColor(child: OrgPart): string {
        return child.colour?.color || '#000000';
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
